import React from 'react'
import { urls } from '../src/utils/config'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles, Grid, Typography, Button } from '@material-ui/core'
import { GetServerSideProps } from 'next'
import Image from 'next/image'
import { getWallets } from '../src/services/wallets'
import { LoadState } from '../types/general'
import { Wallet as IWallet, WalletType } from '../types/wallet'
import WalletGrid from '../src/components/Product/wallets/WalletGrid'
import Nav from '../src/components/Nav'
import ProductNav from '../src/components/Product/Nav'
import WalletGuide from '../src/components/Product/wallets/WalletGuide'
import FAQSection from '../src/components/Product/FAQSection'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Footer from '../src/components/Footer'

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.2) 0.13%, #FFFFFF 100%)',
    overflow: 'auto',
  },
  section: {
    padding: '30px 20px',
    margin: '4rem auto',
    [theme.breakpoints.up('md')]: {
      padding: '30px 80px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '30px 0',
      maxWidth: '1200px',
    },
    '&>div': {
      padding: '0 20px',
    },
    '& h1': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '54px',
      },
    },
    '& h1, & h3': {
      letterSpacing: '-0.055em',
      margin: '1rem 0',
    },
    '& p': {
      fontSize: '16px',
      color: '#7D7787',
    },
  },
  heroContentSection: {},
  heroCTA: {
    textTransform: 'unset',
    margin: '1rem 0',
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',
    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
  listWrapper: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '1020px',
      margin: 'auto',
    },
  },
}))

const faqs = [
  {
    question: 'Can I use every Blockchain with a non-custodial wallet?',
    answer:
      'It depends. Some wallets are multichain meaning they support several blockchains within the same app, others are focused on one only.',
  },
  {
    question: 'What is better: a mobile wallet or browser extension?',
    answer:
      'Because many of the decentralized applications are web based, the most popular wallets are browser extensions like Metamask that can be connected to the site by simple button click. However, mobile wallets are rising in popularity due to their safety and new interoperability standards like WalletConnect.',
  },
  {
    question: 'Do I need to buy a hardware wallet to use with my wallet?',
    answer:
      'Buying a hardware wallet is recommended for larger sums but it also makes using your wallet more cumbersome since there are more steps involved if you want to move funds. Many people recommend using a hardware wallet for your “cold wallet” with funds you don’t move around often and a “hot wallet” without a hardware wallet for your everyday funds you use in DeFi and elsewhere.',
  },
]

interface Props {
  wallets: IWallet[]
  initialLoadState: LoadState
}

const Wallets = ({ wallets }: Props): JSX.Element => {
  const classes = useStyles({})
  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.productExchanges,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters.com - Wallets`}
            description={`Cryptotesters.com.`}
            openGraph={{
              url: `https://cryptotesters.com`,
              title: `Cryptotesters.com - Wallets`,
              description: `Wallets Cryptotesters.com.`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/home.jpg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/home.jpg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.heroSection}>
              <Nav dark />
              <ProductNav />

              <Grid container className={classes.section}>
                <Grid item md={5} xs={12}>
                  <Typography variant="h1">The best non- custodial crypto wallets</Typography>
                  <Typography variant="body2">
                    A non-custodial crypto wallet let’s you hold, send, and receive crypto assets by
                    allowing you to directly interact with the blockchain without any middlemen
                    involved. With a non-custodial wallet you are in full control over your funds. A
                    wallet is your gateway to the world of DeFi and crypto applications.
                  </Typography>
                  <Button
                    color="primary"
                    variant="contained"
                    href="#wallets"
                    className={classes.heroCTA}
                  >
                    Learn more about wallets
                  </Button>
                </Grid>

                <Grid item md={7} xs={12}>
                  <div>
                    <div className={classes.videoWrapper}>
                      <Image src="/phone.png" objectFit="contain" layout="fill" />
                    </div>
                  </div>
                </Grid>
              </Grid>
              <div className={`${classes.section} ${classes.listWrapper}`} id="wallets">
                <Typography align="center" variant="h3">
                  Compare Non Custodial wallets
                </Typography>

                <WalletGrid wallets={wallets} type={WalletType.nonCustodial} />
              </div>
            </div>

            <div>
              <WalletGuide />
              <FAQSection faqs={faqs} />
            </div>
            <QuickLinks />
            <Footer />
          </div>
        </>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  let loadState = LoadState.LOADING
  let wallets: IWallet[] = []

  try {
    wallets = await getWallets('non-custodial-wallets')
    loadState = LoadState.LOADED
  } catch (e) {
    loadState = LoadState.ERROR
  }

  return {
    props: {
      initialLoadState: loadState,
      wallets,
    },
  }
}

export default Wallets
